import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getNewsCategories, getNewsList } from "@/api/newsApi";
import { assetUrl } from "@/utils/assetUrl";

const NEWS_ACCENT = "#64748b";

interface NewsCategory {
  id: string;
  name: string;
}

interface NewsItem {
  id: number;
  category: string;
  categoryName: string;
  title: string;
  summary: string;
  content: string;
  coverImage: string;
  tags: string[];
  publishDate: string;
  readTime: string;
}

export default function NewsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [categories, setCategories] = useState<NewsCategory[]>([]);
  const [newsList, setNewsList] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    getNewsCategories()
      .then((res: any) => setCategories(Array.isArray(res) ? res : []))
      .catch(() => {});
  }, []);

  const fetchNews = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params: any = { pageNum: 1, pageSize: 50 };
      if (activeCategory !== "all") params.category = activeCategory;
      const res: any = await getNewsList(params);
      setNewsList(res.rows || []);
    } catch {
      setError("加载新闻列表失败，请稍后重试");
      setNewsList([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => { fetchNews(); }, [fetchNews]);

  const featured = newsList[0];

  return (
    <main className="min-h-screen bg-[#f8fafc]">
      {/* Hero */}
      <section className="relative bg-[#0d1f3c] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://readdy.ai/api/search-image?query=technology%20news%20media%20background%2C%20digital%20information%20flow%20visualization%2C%20cybersecurity%20industry%20news%2C%20dark%20blue%20technology%20atmosphere%2C%20professional%20media%20background&width=1920&height=500&seq=newshero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0d1f3c]/60 to-[#0d1f3c]"></div>
        </div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#64748b]/20 border border-[#64748b]/40 rounded-full mb-5">
            <span className="text-[#cbd5e1] text-xs font-medium">行业资讯</span>
          </div>
          <h1 className="text-3xl lg:text-5xl font-bold text-white mb-4">
            洞察行业动态，把握安全前沿
          </h1>
          <p className="text-gray-400 text-sm lg:text-base max-w-2xl mx-auto">
            行业动态 · 技术前沿 · 政策法规 · 公司新闻，一站式获取信息安全资讯
          </p>
        </div>
      </section>

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
          <i className="ri-loader-4-line animate-spin mr-2 text-lg"></i>加载中...
        </div>
      )}

      {error && (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
          <div className="bg-red-50 border border-red-100 rounded-xl p-6 text-center text-red-600 text-sm">
            <i className="ri-error-warning-line text-2xl block mb-2"></i>
            {error}
            <button onClick={fetchNews} className="ml-3 underline cursor-pointer">重试</button>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Featured Article */}
          {featured && (
            <section className="py-10 lg:py-14">
              <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <div
                  onClick={() => navigate(`/news/${featured.id}`)}
                  className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-gray-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="flex flex-col lg:flex-row">
                    <div className="w-full lg:w-[480px] h-56 lg:h-72 flex-shrink-0 overflow-hidden">
                      <img
                        src={assetUrl(featured.coverImage)}
                        alt={featured.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 p-7 lg:p-10 flex flex-col justify-center">
                      <div className="flex items-center gap-3 mb-4">
                        <span className="px-3 py-1 text-white text-xs font-medium rounded-full" style={{ backgroundColor: NEWS_ACCENT }}>
                          精选推荐
                        </span>
                        <span className="px-2.5 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: `${NEWS_ACCENT}12`, color: NEWS_ACCENT }}>
                          {featured.categoryName}
                        </span>
                      </div>
                      <h2 className="text-xl lg:text-2xl font-bold text-[#0d1f3c] mb-4 leading-snug">{featured.title}</h2>
                      <p className="text-gray-500 text-sm leading-relaxed mb-5 line-clamp-3">{featured.summary}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center"><i className="ri-calendar-line"></i></div>
                          {featured.publishDate}
                        </span>
                        <span className="flex items-center gap-1">
                          <div className="w-3 h-3 flex items-center justify-center"><i className="ri-time-line"></i></div>
                          阅读约{featured.readTime}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* Category Filter + List */}
          <section className="pb-14">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
              {/* Filter */}
              <div className="flex gap-2 overflow-x-auto mb-8 scrollbar-hide">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className="px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 cursor-pointer border"
                    style={activeCategory === cat.id
                      ? { backgroundColor: NEWS_ACCENT, color: "#fff", borderColor: NEWS_ACCENT }
                      : { backgroundColor: "transparent", color: "#6b7280", borderColor: "#e5e7eb" }
                    }
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Grid */}
              {newsList.length === 0 ? (
                <div className="text-center py-16 text-gray-400 text-sm">
                  <i className="ri-article-line text-4xl block mb-3 text-gray-200"></i>
                  暂无资讯
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {newsList.map((news) => (
                    <div
                      key={news.id}
                      onClick={() => navigate(`/news/${news.id}`)}
                      className="group bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-gray-200 hover:-translate-y-1 transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full h-44 overflow-hidden">
                        <img
                          src={assetUrl(news.coverImage)}
                          alt={news.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2.5 py-0.5 text-xs font-medium rounded-full" style={{ backgroundColor: `${NEWS_ACCENT}12`, color: NEWS_ACCENT }}>
                            {news.categoryName}
                          </span>
                          <span className="text-gray-400 text-xs">{news.publishDate}</span>
                        </div>
                        <h3 className="text-sm font-bold text-[#0d1f3c] mb-2 leading-snug line-clamp-2">{news.title}</h3>
                        <p className="text-xs text-gray-500 leading-relaxed line-clamp-3 mb-3">{news.summary}</p>
                        <div className="flex flex-wrap gap-1.5">
                          {(news.tags || []).map((tag) => (
                            <span key={tag} className="px-2 py-0.5 bg-gray-50 text-gray-400 text-xs rounded-full border border-gray-100">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </>
      )}
    </main>
  );
}
